import {
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
} from '@heroicons/react/24/outline';
import React, { useMemo, useState } from 'react';

import { useDocumentPreview } from '../../hooks/useDocumentPreview';
import type {
  OcrData,
  OcrExpenseField,
} from '../../types';
import { buildHighlightId } from '../../utils/ocr-field-mapping-utils';
import LoadingSpinner from '../LoadingSpinner';

import type { HighlightRect } from './DocumentHighlightOverlay';
import ImageDocumentViewer from './ImageDocumentViewer';
import PdfDocumentViewer from './PdfDocumentViewer';

interface InvoicePreviewPanelProps {
  storageKey: string;
  contentType: string;
  fileName: string;
  ocrData?: OcrData | null;
  /** Field IDs to highlight (from expenseFields) */
  activeFieldIds?: Set<string>;
  onHighlightClick?: (fieldId: string) => void;
}

const SCALE_STEP = 0.25;
const MIN_SCALE = 0.5;
const MAX_SCALE = 3.0;
const DEFAULT_SCALE = 1.0;

/**
 * Main invoice preview container.
 * Picks PDF or image viewer based on contentType,
 * builds highlight data from OCR expense fields.
 */
const InvoicePreviewPanel: React.FC<InvoicePreviewPanelProps> = ({
  storageKey,
  contentType,
  fileName,
  ocrData,
  activeFieldIds,
  onHighlightClick,
}) => {
  const { documentUrl, isLoading, error } = useDocumentPreview(storageKey);
  const [scale, setScale] = useState(DEFAULT_SCALE);

  const isPdf = contentType === 'application/pdf';

  // Build highlight rects from expense fields with bounding boxes
  const { imageHighlights, pdfHighlightsByPage } = useMemo(() => {
    if (!ocrData?.expenseFields) {
      return { imageHighlights: [], pdfHighlightsByPage: new Map() };
    }

    const byPage = new Map<number, HighlightRect[]>();
    const allHighlights: HighlightRect[] = [];

    ocrData.expenseFields.forEach((field: OcrExpenseField, index: number) => {
      // Only highlight fields that map to booking data
      if (!field.valueBoundingBox || !field.bookingFieldMapping) return;

      const id = buildHighlightId(field.fieldType, index);
      const rect: HighlightRect = {
        id,
        boundingBox: field.valueBoundingBox,
        isActive: activeFieldIds?.has(id),
      };

      allHighlights.push(rect);

      const pageNum = field.pageNumber;
      const pageRects = byPage.get(pageNum) ?? [];
      pageRects.push(rect);
      byPage.set(pageNum, pageRects);
    });

    return { imageHighlights: allHighlights, pdfHighlightsByPage: byPage };
  }, [ocrData, activeFieldIds]);

  if (isLoading) {
    return (
      <div className='flex items-center justify-center h-full bg-gray-50 rounded-lg'>
        <LoadingSpinner size='lg' />
      </div>
    );
  }

  if (error || !documentUrl) {
    return (
      <div className='flex items-center justify-center h-full bg-gray-50 rounded-lg text-red-500 text-sm'>
        {error?.message ?? 'Failed to load document'}
      </div>
    );
  }

  return (
    <div className='flex flex-col h-full bg-gray-50 rounded-lg'>
      {/* Zoom controls */}
      <div className='flex items-center justify-between px-3 py-2 border-b border-gray-200 bg-white rounded-t-lg'>
        <span className='text-xs text-gray-500 truncate' title={fileName}>
          {fileName}
        </span>
        <div className='flex items-center gap-1'>
          <button
            className='p-1 rounded hover:bg-gray-100 disabled:opacity-40'
            onClick={() => setScale(s => Math.max(MIN_SCALE, s - SCALE_STEP))}
            disabled={scale <= MIN_SCALE}
            aria-label='Zoom out'
          >
            <MagnifyingGlassMinusIcon className='w-4 h-4 text-gray-600' />
          </button>
          <span className='text-xs text-gray-500 min-w-[3rem] text-center'>
            {Math.round(scale * 100)}%
          </span>
          <button
            className='p-1 rounded hover:bg-gray-100 disabled:opacity-40'
            onClick={() => setScale(s => Math.min(MAX_SCALE, s + SCALE_STEP))}
            disabled={scale >= MAX_SCALE}
            aria-label='Zoom in'
          >
            <MagnifyingGlassPlusIcon className='w-4 h-4 text-gray-600' />
          </button>
        </div>
      </div>

      {/* Document viewer */}
      <div className='flex-1 overflow-auto p-4'>
        {isPdf ? (
          <PdfDocumentViewer
            url={documentUrl}
            scale={scale}
            highlightsByPage={pdfHighlightsByPage}
            onHighlightClick={onHighlightClick}
          />
        ) : (
          <div style={{ transform: `scale(${scale})`, transformOrigin: 'top left' }}>
            <ImageDocumentViewer
              url={documentUrl}
              alt={fileName}
              highlights={imageHighlights}
              onHighlightClick={onHighlightClick}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoicePreviewPanel;
